<template>
  <div class="test" :noShadow="true" v-if="loaded">
    <div class="main-title">
      <h4>{{ $t('Review Comments') }}</h4>
    </div>

    <div class="sub-title">
      <h5>{{ $t('Rejection Reasons') }}</h5>
    </div>

    <section class="rejection-reasons">
      <ul class="">
        <li class="reasons-list" v-for="reason in reasons" :key="'reason-' + reason.id">
          <vs-checkbox v-model="form.review_reason_ids" :vs-value="reason.id" class="ml-0" style="color: #c2c2c2;">
            <p class="ml-3 checkbox-option-text">
              {{ reason.reason }}
            </p>
          </vs-checkbox>
        </li>
      </ul>

      <h3 class="reasons-err" v-if="errors.has('review_reason_ids')">{{errors.first('review_reason_ids')}}</h3>

      <textarea v-model="form.summary" placeholder="Other comments" maxLength="500"/>
    </section>

    <section class="titles-recommendition">
      <h5 class="sub-title title-recommendition-sub-title">
        {{ $t('Title’s recommendations') }}
      </h5>

      <div class="" v-for="(title,index) in titles" :key="`Option ${index+1}`">
        <div class="flex space-x-0 justify-items-end">
          <speakol-input
            maxLength="70"
            class="sug-title"
            v-model="titles[index].value"
            :placeholder="`Option ${index+1}`"
            :label="`Option ${index+1}`"
            validation="max:70"
            :correlationKey="`Option ${index+1}`"
          >
          </speakol-input>

          <span class="flex space-x-8 items-center pl-5 add-title">
            <vs-icon :class="{'cursor-not-allowed': !title.value}" class="plus" v-if="titles.length === index + 1" icon="speakol-plus" icon-pack="speakol" :color="title.value? '#0092d7':'#c2c2c2'" @click="addTitle(title)"></vs-icon>

            <vs-icon class="remove" v-if="titles.length > 1" icon="speakol-cancel" icon-pack="speakol" color="#DB2828" @click="removeTitle(index)"></vs-icon>
          </span>
        </div>
      </div>
    </section>

    <section class="photo-recommendition" v-if="ad.adset.campaign.display_type_id !== $constants.CAMPAIGN_TYPES_IDS.VIDEO">
      <h5 class="sub-title">
        {{ $t('Photo Recommendations') }}
      </h5>
      <div class="grid gap-8 md:grid-cols-3 grid-col-1">
        <span v-for="(image,index) in images" :key="image.id">
          <Upload
            :type="'image'"
            v-model="images[index]['value']"
            :accept="'image/x-png,image/jpeg'"
            :maxSize="0.3"
            :validateOnDrop="handleValidateOnDrop"
            :disabled="false"
            :showTitle="false"
            :placeholder="placeholder"
            @delete="removeImg({...$event, index})"
          >
          </Upload>
        </span>
        <span @click="addImage()" class="flex items-center border-gray-100 w-10" :class="[isLastImageContainerFilled ? 'cursor-pointer' :'cursor-not-allowed']">
          <vs-icon icon="speakol-plus" icon-pack="speakol" :color="isLastImageContainerFilled ? '#0092d7':'#c2c2c2'" class="new-img-btn"></vs-icon>
        </span>
      </div>
    </section>

    <section class="text-right space-x-4 actions">
      <button class="action-cancel" @click="close">
      {{ $t('Cancel') }}
      </button>
      <button
        type="filled"
        @click="submitReview"
        :disabled="isSubmitBtnDisabled"
        :class="[isSubmitBtnDisabled? 'disabled-action-submit': 'action-submit']">
        {{ $t('Submit') }}
      </button>
    </section>
  </div>
</template>

<script>
import Upload from '@/views/campaigns/components/Upload.vue'
import { objectToFormData } from '@/helpers/objectToFormData.js'
import {cloneDeep} from 'lodash'

import {getReviewReasons} from '@/utils/ReviewReasons'

export default {
  components: { Upload },
  props: {
    adId: {
      required: true
    },
    adAcceptanceStatus: {
      default: null
    }
  },
  data () {
    return {
      showRejectionForm: true,
      ad: null,
      images: [],
      titles: [],
      loaded: false,
      form: this.getFormDefaults(),
      rejectionFeedback: null,
      placeholder: {
        format: 'Format：jpg. png.',
        size: 'Size：300 KB max.',
        dimensions: 'Dims：640*360 px max.',
        classes: 'placeholder-style'
      },
      isFirstLoad: true
    }
  },

  computed: {
    isLastImageContainerFilled () {
      const lastImgIndex = this.images.length

      return this.images[lastImgIndex - 1] && this.images[lastImgIndex - 1].value
    },

    reasons () {
      return getReviewReasons(
        this.$constants.AD_REVIEW_REASON_TYPE_IDS.REJECT,
        this.ad.adset.campaign.display_type_id === this.$constants.CAMPAIGN_TYPES_IDS.VIDEO
      )
    },

    isSubmitBtnDisabled () {
      return this.isFirstLoad && this.ad.acceptance_status === this.$constants.AD_ACCEPTANCE_STATUSES.REJECTED
    }
  },

  watch: {
    form: {
      handler () {
        this.isFirstLoad = false
      },
      deep: true
    }
  },

  async created () {
    this.ad = await this.getAdFeedback(this.adId)

    this.form = await this.setFormData()

    this.isFirstLoad = true

    this.loaded = true
  },

  methods: {
    getFormDefaults () {
      return {
        ad_id: this.adId,
        acceptance_status: 0,
        summary: '',
        review_reason_ids: [],
        recommendations: []
      }
    },

    close () {
      this.errors.clear()

      this.form = this.getFormDefaults()

      this.$emit('cancel')
    },

    async getAdFeedback (adId) {
      const response = await window.axios.get(`/feedbacks/ads/${adId}`, {
        params: {
          type: this.$constants.FEEDBACK_TYPES.REJECT
        }
      })

      return response.data.payload
    },

    async getRecommendationItemsOfSubject (subject) {
      if (this.ad.feedback) {
        const items = await this.getFlatRecsOfSubject(this.ad.feedback.recommendations, subject)

        if (items && items.length > 0) {
          return items.map((item) => {
            return {
              id: Math.random(),
              value: item.value
            }
          }, items)
        }
      }

      return [{id: Math.random(), value: null}]
    },

    async setFormData () {
      this.form.summary = this.ad.feedback ? this.ad.feedback.summary : ''

      this.form.review_reason_ids = []

      if (this.ad.feedback && this.ad.feedback.review_reasons) {
        this.form.review_reason_ids = this.ad.feedback.review_reasons.map(el => el.id)
      }

      this.form.recommendations =
        [
          {
            subject: 'title',
            required: true,
            values: this.titles = await this.getRecommendationItemsOfSubject('title')
          },
          {
            subject: 'image',
            required: true,
            values: this.images = await this.getRecommendationItemsOfSubject('image')
          }
        ]

      this.isFirstLoad = true

      return this.form
    },

    removeImg (...params) {
      if (params[0]['type'] === 'remove') {
        this.images.splice(params[0]['index'], 1)

        if (this.images.length === 0) {
          this.addImage()
        }
      }
    },

    async submitReview () {
      try {
        this.errors.clear()

        const formToSave = cloneDeep(this.form)

        formToSave.recommendations = formToSave.recommendations.filter(el => el.values[0]['value'] !== null)

        const response = await window.axios(`/campaigns/${this.$route.params.id}/review-ad`, {
          method: 'POST',
          data: objectToFormData(formToSave),
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.showRejectionForm = false

        this.$emit('acceptance-status-updated', response.data.payload)
      } catch (error) {
        this.$setLaravelValidationErrorsFromResponse(error.response.data)
      }
    },

    getFlatRecsOfSubject (recommendations, subject) {
      if (recommendations) {
        const recommendationOfSubject = recommendations.find(el => el.subject === subject)

        return recommendationOfSubject ? recommendationOfSubject.recommendation_items : []
      }
    },

    handleValidateOnDrop (file) {
      return /\.(jpeg|png|jpg)$/.test(file.name)
    },

    addImage () {
      if (!this.isLastImageContainerFilled && this.images && this.images.length > 0) {
        return
      }

      this.images.push({
        id: Math.random(),
        value: null
      })
    },

    addTitle (title) {
      if (!title.value) {
        return
      }

      this.titles.push({
        value: null
      })
    },

    removeTitle (index) {
      this.titles.splice(index, 1)
    }
  }
}
</script>

<style lang="scss">

.vs-popup--content::-webkit-scrollbar-track,
.textarea::-webkit-scrollbar-track {
  border-radius: 10px !important;
  background-color: #f5f5f5 !important;
}

.vs-popup--content::-webkit-scrollbar,
textarea::-webkit-scrollbar {
  width: 6px !important;
  height: 6px !important;
  background-color: #f5f5f5 !important;
}

.vs-popup--content::-webkit-scrollbar-thumb,
textarea::-webkit-scrollbar-thumb {
  border-radius: 10px !important;
  background-color: lightgray !important;
}

.rejection-reasons {
  textarea {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    height: 140px;
    max-height: 140px;
    min-height: 140px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 32px;
    margin-top: 20px;
    font-family: "Poppins", sans-serif;
  }

  textarea::placeholder {
    color: #c2c2c2;
  }

  .reasons-list:not(:last-child) {
    margin-bottom: 19px;
  }
}

.photo-recommendition {
  margin-top: 10px;

  h5 {
    margin-bottom: 18px;
  }
}

.actions {
  margin-top: 18px;
}

.vs-popup--content {
  padding: 27px 27px 27px 32px !important;
}

.title-recommendition-sub-title {
  margin-bottom: 18px;
  font-size: 18px;
}

.titles-recommendition {
  .label {
    margin-bottom: 8px;
  }

  input:focus {
    box-shadow: none !important;
  }
}

.ad-recommendations {
  font-size: 12px !important;
  font-family: Poppins;

  .vs-popup {
    width: 558px !important;
  }

  .review-img,
  .upload-component,
  .image-container {
    font-size: 12px !important;
    width: 154px !important;
    height: 116px !important;
    border-radius: 10px !important;
  }

  .image-container {
    .cursor-pointer {
      right: 8px !important;
      top: 8px !important;
    }
  }

  .placeholder-style {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    text-align: center !important;
  }

  .icon-container {
    height: 24px !important;
    padding: 2px !important;
  }

  .upload-icon,
  .upload-icon-hover {
    width: 20px !important;
    height: 20px !important;
  }

  .add-title {
    height: 40px;
    margin-top: 29px;
    cursor: pointer !important;

    .remove,
    .plus {
      font-size: 17px !important;
    }
  }

  .title {
    @apply font-medium leading-10 text-speakol-black-color;
  }

  .main-title {
    h4 {
      @extend .title;

      font-size: 24px;
    }
  }

  .sub-title {
    h5 {
      @extend .title;

      font-size: 18px;
      margin-top: 18px;
      margin-bottom: 16px;
      font-weight: 500;
    }
  }

  .checkbox-option-text {
    color: #323232;
    font-size: 14px;
  }

  .vs-popup--content {
    overflow-y: auto;
  }

  .sug-title {
    min-width: 80%;
    margin-bottom: 28px;
  }

  .new-img-btn {
    border: 1px solid rgba(194, 194, 194, 0.4);
    padding: 10px;
    border-radius: 4px;
    // width: 40px;
    // height: 40px;
    font-size: 18px !important;
  }

  .reasons-err {
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    color: #db2828;
    margin-top: 15px;
  }

  @media (max-width: 768px) {
    .sug-title {
      min-width: 72%;
    }

    .vs-popup--content {
      padding: 24px !important;
    }

    .upload-component,
    .image-container {
      width: 100% !important;
    }

    .ad-recommendations .review-img,
    .ad-recommendations .upload-component,
    .ad-recommendations .image-container {
      width: 100% !important;
    }

    .review-img {
      width: 100% !important;
    }

    .vs-checkbox {
      padding: 7px !important;
      width: unset !important;
      height: unset !important;
      position: relative;
      top: 1px;
    }

    .vs-checkbox-default {
      align-items: start !important;
    }

    .actions {
      display: flex;
      flex-direction: column-reverse;
    }

    .action-submit {
      margin-bottom: 16px;
    }

    .action-submit,
    .action-cancel {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}
</style>

